import { keepPreviousData, useQuery } from '@tanstack/react-query'
import type { ReactNode } from 'react'
import type { PageQueryOptions, PageRequest } from '../api/opteemum'
import { Content } from '../ui/Shell'
import { DataTable, useTableState } from '../ui/DataTable'
import { DeviceWorkspace } from '../devices/DeviceWorkspace'
import type { Column } from './columns'

/**
 * Listele Opteemum in afara celor cinci device-lists: Unassigned,
 * Applications, Schedule, Remote Connect. Acelasi DataTable, aceleasi id-uri.
 */

export function OpteemumTablePage({
  id,
  columns,
  queryOf,
  detailsFrom,
  details,
  headerButtons,
  selected,
  onSelected,
  rowKey,
}: {
  id: string
  columns: Column[]
  queryOf: (r: PageRequest) => PageQueryOptions
  /** Randul desfacut e fisa aparatului, deschisa din modulul asta. */
  detailsFrom?: string
  /** Randul desfacut e altceva decat o fisa de aparat - Applications, Schedule. */
  details?: (row: Record<string, unknown>) => ReactNode
  /** Butoanele modulului din bara de sus - `.dataTableA` din machetele vechi. */
  headerButtons?: ReactNode
  /**
   * Bifele de pe randuri, cand lista are o coloana de ales.
   *
   * Starea sta la pagina, nu aici: butonul care lucreaza pe alegere (Remove
   * schedules) sta si el tot acolo, si amandoua trebuie sa se uite la acelasi
   * numar. `rowKey` spune ce se bifeaza.
   */
  selected?: Set<number>
  onSelected?: (next: Set<number>) => void
  rowKey: (row: Record<string, unknown>) => number
}) {
  const [state, setState] = useTableState(id, { start: 0, length: 25, order: { column: 2, dir: 'desc' }, search: '' })
  const orderColumn = columns.find((c) => c.index === state.order.column) ?? columns[2]
  const query = useQuery({
    ...queryOf({
      draw: 1,
      start: state.start,
      length: state.length,
      orderColumn: orderColumn.index,
      orderName: orderColumn.name,
      orderDir: state.order.dir,
      search: state.search,
    }),
    placeholderData: keepPreviousData,
  })
  const raw = (query.data?.data ?? []) as Record<string, unknown>[]
  const rows = selected ? raw.map((r) => ({ ...r, __checked: selected.has(rowKey(r)) })) : raw

  /*
   * Bifele sunt HTML scris de coloana, ca in DataTables - deci clicul se prinde
   * pe deasupra tabelului, dupa clasa, nu cu un `onChange` pe fiecare casuta.
   * Cea din cap (`allInputs` / `allDevices`) le bifeaza pe toate de pe pagina.
   */
  const onClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement
    if (!onSelected || !(target instanceof HTMLInputElement)) return
    if (target.classList.contains('oneInput') || target.classList.contains('oneDevice')) {
      const next = new Set(selected ?? [])
      const value = Number(target.value)
      if (target.checked) next.add(value)
      else next.delete(value)
      onSelected(next)
    } else if (target.classList.contains('allInputs') || target.classList.contains('allDevices')) {
      onSelected(target.checked ? new Set(raw.map(rowKey)) : new Set())
    }
  }

  return (
    <Content>
      <div onClick={onClick}>
      <DataTable
        id={id}
        columnTools
        columns={columns}
        rows={rows}
        total={query.data?.recordsTotal ?? 0}
        fetching={query.isFetching}
        error={query.isError ? (query.error as Error).message : null}
        onRetry={() => void query.refetch()}
        state={state}
        onState={setState}
        rowId={(r) => String(r.DT_RowId ?? `row_${rowKey(r)}`)}
        details={details ?? (detailsFrom ? (row) => <DeviceWorkspace id={rowKey(row)} from={detailsFrom} /> : undefined)}
        headerButtons={headerButtons}
        footerButtons={{ copy: true, colvis: true, refresh: () => void query.refetch() }}
      />
      </div>
    </Content>
  )
}
